/**
 * Where dictation anywhere is in its flow, mirrored so every window (and the
 * indicator on the cursor's display) sees the same phase. Codes only: what
 * was said never lives here.
 *
 * Only the owner window's controller writes it
 * (`dictation-anywhere-controller.ts`).
 */
import { create } from "zustand";
import { mirroredStore } from "@/lib/mirrored-store";

export type DictationAnywherePhase =
  | "idle"
  | "listening"
  | "transcribing"
  | "enhancing"
  | "inserting"
  | "done"
  | "failed";

/** Why a finished flow needs the user's attention; null when it does not. */
export type DictationAnywhereOutcome = "copied" | "not_improved";

export type DictationAnywhereFailure =
  /** Another voice input owns the microphone, or dictation is unavailable. */
  | "not_available"
  /** The transcript was empty. */
  | "nothing_heard"
  /** No transcript arrived in time; a late one is still copied. */
  | "no_transcript"
  /** The recording failed to start or broke off. */
  | "capture_failed"
  /** The text could not be saved, copied or pasted. */
  | "delivery_failed";

export type DictationAnywhereState = {
  phase: DictationAnywherePhase;
  outcome: DictationAnywhereOutcome | null;
  reason: DictationAnywhereFailure | null;
  /** The name of the numbered prompt the user stopped with, shown while it
   *  rewrites; null for a Default stop. Never transcript text. */
  promptName: string | null;
  /** Marked screenshots saved with a finished flow's dictation but not
   *  pasted: they wait in Recent dictations. 0 when none. */
  unpastedImages: number;
};

/** `useDictation.ownerId` while dictation anywhere records: no composer owns
 *  the recording, so every composer mic shows as taken. */
export const DICTATION_ANYWHERE_OWNER_ID = "dictation-anywhere";

export const DICTATION_ANYWHERE_IDLE: DictationAnywhereState = {
  phase: "idle",
  outcome: null,
  reason: null,
  promptName: null,
  unpastedImages: 0,
};

export const useDictationAnywhereStore = create<DictationAnywhereState>()(
  mirroredStore("dictationAnywhere", () => ({ ...DICTATION_ANYWHERE_IDLE })),
);

const WORKING_PHASES: ReadonlySet<DictationAnywherePhase> = new Set([
  "listening",
  "transcribing",
  "enhancing",
  "inserting",
]);

/** The flow is between its trigger and its result. Other voice inputs stay
 *  off meanwhile; the result pill (`done` / `failed`) does not hold them. */
export function isDictationAnywhereBusy(): boolean {
  return WORKING_PHASES.has(useDictationAnywhereStore.getState().phase);
}

type FinalHandler = (text: string) => void;

let finalHandler: FinalHandler | null = null;

/** Installed by the owner window's controller for as long as it is mounted. */
export function setDictationAnywhereFinalHandler(
  next: FinalHandler | null,
): void {
  finalHandler = next;
}

/** Hands a dictation anywhere final transcript to the flow. Called by the
 *  final routing in the owner window only; without a controller it is dropped. */
export function deliverDictationAnywhereFinal(text: string): void {
  finalHandler?.(text);
}
